import React, { useEffect, useState } from 'react';

import { GetTimelineVariables } from 'features/deals/DealStages/__generated__/fragments';
import { GET_DEAL_TIMELINE } from 'features/deals/DealStages/fragments';
import { useDialog, Dialog, Button, DialogActions } from 'ui-kit';
import DealButton from 'ui-kit/DealButton/DealButton';

import { useMutation } from '@apollo/client';

import { SubmitBidInput } from '__generated__/globalTypes';
import { SubmitBid, SubmitBidVariables } from 'mutation/__generated__/submitBid';
import { SUBMIT_BID } from 'mutation/submitBid';

import { BidConfirmationDialog } from './BidConfirmationDialog';
import { BidButtonsGroup } from './BiddingAndPricingSection.styles';
import { useBiddingPricingContext } from './BiddingPricingContext';
import { BidSucceededDialog } from './BidSucceededDialog';

export const BidActionButtons = () => {
  const { selectedCarve, dealId, bidDetails, flushCarveStipulations, flushCustomStipulations } =
    useBiddingPricingContext();

  const [errorMessage, setErrorMessage] = useState<string>('');

  // Track if local bid percentage matches server state
  const localBidBasisPoints = bidDetails?.carve?.stipulations?.bid_basis_points;
  const serverBidBasisPoints = selectedCarve?.stipulations?.bid_basis_points;
  const isBidPercentSynced = localBidBasisPoints === serverBidBasisPoints;

  // Debug logging to understand sync state
  useEffect(() => {
    if (localBidBasisPoints !== undefined && serverBidBasisPoints !== undefined) {
      console.log('Bid sync state:', {
        local: localBidBasisPoints,
        server: serverBidBasisPoints,
        synced: isBidPercentSynced,
      });
    }
  }, [localBidBasisPoints, serverBidBasisPoints, isBidPercentSynced]);

  // Check if we have all required data
  const hasRequiredData =
    bidDetails &&
    bidDetails.carve?.stipulations?.bid_basis_points != null &&
    selectedCarve?.stipulations != null &&
    selectedCarve?.id != null;

  // Show different states based on synchronization
  const isWaitingForSync = hasRequiredData && !isBidPercentSynced;
  const canSubmit = hasRequiredData && isBidPercentSynced;

  // Button is only enabled when we can actually submit (synced state)
  const submitEnabled = canSubmit;

  // Auto-flush when values are out of sync
  useEffect(() => {
    if (isWaitingForSync) {
      // Automatically trigger sync when values don't match
      flushCarveStipulations();
      flushCustomStipulations();
    }
  }, [isWaitingForSync, flushCarveStipulations, flushCustomStipulations]);

  const getDealTimelineVariables: GetTimelineVariables = {
    id: dealId,
  };

  const [submitBid] = useMutation<SubmitBid, SubmitBidVariables>(SUBMIT_BID, {
    refetchQueries: [
      {
        query: GET_DEAL_TIMELINE,
        variables: getDealTimelineVariables,
      },
    ],
  });

  const bidConfirmationDialog = useDialog();
  const bidSucceededDialog = useDialog();
  const errorDialog = useDialog();

  if (!dealId) {
    return <p>Invalid Deal ID</p>;
  }
  const onSubmitBid = () => {
    // Button is only enabled when synced, so we can directly open the modal
    bidConfirmationDialog.openDialog();
  };

  const onBidConfirmed = () => {
    bidConfirmationDialog.closeDialog();
    const bidBasisPoints = selectedCarve?.stipulations?.bid_basis_points;

    if (
      bidDetails &&
      selectedCarve &&
      selectedCarve.stipulations &&
      bidBasisPoints !== null &&
      bidBasisPoints !== undefined &&
      selectedCarve.id
    ) {
      const input: SubmitBidInput = {
        deal_id: dealId,
        bid_basis_points: bidBasisPoints,
        carve_id: selectedCarve.id,
        document_ids: bidDetails.stipulations.document_ids,
      };
      console.log('Sending bid to server', input);
      submitBid({
        variables: {
          input,
        },
      })
        .then(() => {
          bidSucceededDialog.openDialog();
        })
        .catch((err) => {
          setErrorMessage(err.message);
          errorDialog.openDialog();
        });
    }
  };

  return (
    <>
      <BidButtonsGroup>
        <DealButton
          data-testid="bid-submit-button"
          disabled={!submitEnabled}
          onClick={onSubmitBid}
          variant={canSubmit ? 'success' : 'primary'}
          label="submit_bid"
          loading={isWaitingForSync}
          loadingLabel="Saving Stips..."
        >
          Submit Bid
        </DealButton>
      </BidButtonsGroup>

      {bidDetails && selectedCarve && (
        <Dialog dialog={bidConfirmationDialog}>
          <BidConfirmationDialog
            bidDetails={bidDetails}
            selectedCarve={selectedCarve}
            onCancel={() => bidConfirmationDialog.closeDialog()}
            onConfirm={() => onBidConfirmed()}
          />
        </Dialog>
      )}

      <Dialog dialog={bidSucceededDialog}>
        <BidSucceededDialog
          onCancel={() => bidSucceededDialog.closeDialog()}
          onConfirm={() => bidSucceededDialog.closeDialog()}
        />
      </Dialog>

      <Dialog dialog={errorDialog}>
        <div>ERROR: {errorMessage}</div>
        <DialogActions>
          <Button size="medium" label="OK" onClick={() => errorDialog.closeDialog()}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
